import React, { useCallback, useState } from "react";
import {
    PaymentElement,
    LinkAuthenticationElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import type { StripeError, StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
    logStripeCheckoutEvent,
    stripeErrorSnapshot,
    type StripeCheckoutLogContext,
} from "./stripe-checkout-log";

export interface StripePaymentFormProps {
    /** Called before confirmPayment (e.g. persist checkout for redirect return). */
    onBeforeConfirm?: () => void;
    /** Called after Stripe confirms payment — receives the PaymentIntent data. */
    onPaymentSuccess?: (paymentIntent: { id: string; status: string;[key: string]: any }) => void;
    /** Return URL after redirect-based payment methods (iDEAL, etc.). */
    returnUrl?: string;
    /** Pre-fill the email field. */
    defaultEmail?: string;
    /** Pre-fill the name field in billing details. */
    defaultName?: string;
    /** Server logging context (optional). */
    logContext?: StripeCheckoutLogContext;
}

function formatStripeUserMessage(error: StripeError | undefined): string {
    if (!error) return "Payment failed.";
    const decline = (error as StripeError & { decline_code?: string }).decline_code;
    const parts = [error.message, decline ? `(${decline})` : null].filter(Boolean);
    return parts.join(" ") || "Payment failed.";
}

/**
 * Embedded Stripe payment form using PaymentElement.
 * Must be rendered inside a Stripe `<Elements>` provider.
 */
export function StripePaymentForm({
    onBeforeConfirm,
    onPaymentSuccess,
    returnUrl,
    defaultEmail,
    defaultName,
    logContext,
}: StripePaymentFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

    const log = useCallback(
        (event: string, data?: Record<string, unknown>, level: 'info' | 'warn' | 'error' = 'info') => {
            if (logContext) {
                void logStripeCheckoutEvent(logContext, event, data, level);
            }
        },
        [logContext],
    );

    const handleSubmit = async () => {
        if (!stripe || !elements || !isPaymentElementReady) {
            log('pay_blocked', { reason: 'not_ready', hasStripe: !!stripe, hasElements: !!elements, isPaymentElementReady }, 'warn');
            return;
        }

        setIsLoading(true);
        setMessage(null);
        log('pay_submit_start', { defaultEmail: defaultEmail ? '[set]' : '[empty]', defaultName: defaultName ? '[set]' : '[empty]' });

        const { error: submitError } = await elements.submit();
        if (submitError) {
            const snap = stripeErrorSnapshot(submitError);
            log('elements_submit_failed', snap, 'warn');
            setMessage(formatStripeUserMessage(submitError));
            setIsLoading(false);
            return;
        }

        onBeforeConfirm?.();
        log('confirm_payment_start', {});

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: returnUrl ?? `${window.location.origin}/completion`,
            },
            redirect: "if_required",
        });

        if (error) {
            const snap = stripeErrorSnapshot(error);
            log('confirm_payment_failed', snap, 'error');
            setMessage(formatStripeUserMessage(error));
        } else if (paymentIntent) {
            log('confirm_payment_succeeded', {
                piId: paymentIntent.id,
                status: paymentIntent.status,
                amount: paymentIntent.amount,
                currency: paymentIntent.currency,
            });
            onPaymentSuccess?.({
                id: paymentIntent.id,
                status: paymentIntent.status,
            });
        } else {
            log('confirm_payment_no_result', {}, 'warn');
        }

        setIsLoading(false);
    };

    return (
        <div className="space-y-4">
            <LinkAuthenticationElement
                id="link-authentication-element"
                options={defaultEmail ? { defaultValues: { email: defaultEmail } } : undefined}
                onChange={(event) => {
                    log('link_auth_change', {
                        complete: event.complete,
                        empty: event.empty,
                        email: event.value?.email ? '[set]' : '[empty]',
                    });
                }}
            />
            <PaymentElement
                id="payment-element"
                onReady={() => {
                    setIsPaymentElementReady(true);
                    log('payment_element_ready', {});
                }}
                onLoadError={(event) => {
                    setIsPaymentElementReady(false);
                    const snap = stripeErrorSnapshot(event.error);
                    log('payment_element_load_error', snap, 'error');
                    setMessage(event.error?.message ?? "Failed to load payment form.");
                }}
                onChange={(event: StripePaymentElementChangeEvent) => {
                    log('payment_element_change', {
                        complete: event.complete,
                        empty: event.empty,
                        collapsed: event.collapsed,
                        valueType: event.value?.type,
                    });
                }}
                onEscape={() => log('payment_element_escape', {})}
                onLoaderStart={() => log('payment_element_loader_start', {})}
                options={{
                    layout: {
                        type: "accordion",
                        defaultCollapsed: false,
                    },
                    defaultValues: {
                        billingDetails: {
                            name: defaultName ?? "",
                            email: defaultEmail ?? "",
                        },
                    },
                    business: { name: "PolyMech" },
                }}
            />

            <Button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || !stripe || !elements || !isPaymentElementReady}
                className="w-full"
                size="lg"
            >
                {isLoading ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing…
                    </>
                ) : (
                    "Pay now"
                )}
            </Button>

            {message && (
                <p className="text-sm text-destructive text-center">{message}</p>
            )}
        </div>
    );
}
